import { useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useHistory } from 'react-router';
import { useToasts } from 'react-toast-notifications';
import CustomerPortalComponent from '../../common/CustomerPortalComponent';
import Button from '../../components/Button';
import Card, { CardDescription, CardFooter, CardFooterDate, CardHeader, CardMenu } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import Error from '../../components/Error';
import Header from '../../components/Header';
import IconCustomerPortal from '../../components/icons/IconCustomerPortal';
import Loader from '../../components/Loader';
import Modal, { MODAL_STYLES } from '../../components/Modal';
import { ROUTES } from '../../routes/routes';
import { CustomerPortal } from '../../types/customerPortal';
import { useAxios } from '../../utils/hooks';
import { withCustomerPortalIdParam } from '../PricingPages/NewPage/utils';

export default function CustomerPortals() {
    const history = useHistory();
    const axios = useAxios();
    const { addToast } = useToasts();

    const [portals, setPortals] = useState<CustomerPortal[]>();
    useEffect(() => {
        if (!axios.current) {
            return;
        }

        axios.current
            .get<CustomerPortal[]>('/customer-portals')
            .then((res) => setPortals(res.data))
            .catch((err) =>
                addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
            );
    }, [axios, addToast]);

    const handleCreate = () => {
        history.push(ROUTES.CUSTOMER_PORTALS_NEW);
    };

    const handleDelete = useCallback(
        ({ id }: CustomerPortal) =>
            axios.current?.delete(`/customer-portals/${id}`).then(() => {
                setPortals(portals?.filter((portal) => portal.id !== id));
            }) ?? Promise.resolve(),
        [axios, portals]
    );

    return (
        <div className="p-4 w-full text-center">
            <Header>
                <IconCustomerPortal className="inline w-6 mr-3" /> Customer Portals
            </Header>
            <div className="p-4">
                {!portals && <Loader />}
                {portals && portals.length === 0 && (
                    <EmptyState
                        body={
                            <div className="md:w-1/2 m-auto py-4">
                                <h4 className="text-xl">No Customer Portals Found</h4>
                                <span className="mt-4">
                                    Customer Portals allow your customers to manager their subscription, upgrade their
                                    plan and update their card details.
                                </span>
                                <div className="md:w-64 m-auto mt-6">
                                    <Button onClick={handleCreate}>New Customer Portal</Button>
                                </div>
                            </div>
                        }
                    />
                )}
                {portals && portals.length > 0 && (
                    <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-4 space-y-4 pt-2 px-0 md:px-4 relative">
                        {portals.map((portal) => (
                            <PortalInfo key={portal.id} portal={portal} onDelete={() => handleDelete(portal)} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function PortalInfo(props: { portal: CustomerPortal; onDelete: () => Promise<void> }) {
    const history = useHistory();
    const handleClick = () => {
        history.push(withCustomerPortalIdParam(ROUTES.CUSTOMER_PORTALS_CONFIG, props.portal));
    };
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string>();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleConfirmDelete = () => {
        setIsDeleting(true);
        props.onDelete().catch((err) => {
            setIsDeleting(false);
            setError('Something went wrong. Please try again.');
        });
    };

    const handleDeleteClick = (closeMenu: () => void) => {
        closeMenu();
        setShowDeleteModal(true);
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
    };

    return (
        <Card>
            <CardHeader onClick={handleClick}>
                <div className="pt-8" style={{ zoom: '70%' }}>
                    <CustomerPortalComponent customerPortal={props.portal} disabled poweredBy={false} />
                </div>
            </CardHeader>
            <CardDescription
                title={
                    <button
                        className="font-medium text-lg inline-block mb-2 pointer hover:text-blue-400"
                        onClick={handleClick}
                    >
                        Portal for {props.portal.pricingPageName}
                    </button>
                }
            />
            <CardFooter>
                <CardFooterDate date={props.portal.createdAt} />
                <CardMenu items={[{ title: 'Delete', onClick: handleDeleteClick }]} />
                <ReactModal isOpen={showDeleteModal} style={MODAL_STYLES}>
                    <Modal
                        type="WARN"
                        title="Delete Customer Portal"
                        body={
                            isDeleting ? (
                                <Loader small />
                            ) : (
                                <>
                                    <span>
                                        {
                                            /*props.portal.status === 'ACTIVE'*/ false ? (
                                                <strong>This Customer Portal is Live! </strong>
                                            ) : (
                                                ''
                                            )
                                        }
                                        Are you sure you want to delete this Customer Portal? All of your data will be
                                        permanently removed. This action cannot be undone.
                                    </span>
                                    {error && <Error message={error} />}
                                </>
                            )
                        }
                        confirmButtonText="Delete"
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCloseModal}
                    />
                </ReactModal>
            </CardFooter>
        </Card>
    );
}
